import {
  IListViewTableColumns,
  IListViewTableRow,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { orderDirection } from '~/src/dash/views/platform-connect/common';
import {
  convertRuleTypeToString,
  IInventoryRules,
  RuleType,
} from '~/src/dash/views/inventory-rules/common';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import { DateTimeSecondsRender } from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import {
  generateContentCategoriesOptionsList,
  generateContentGenreOptionsList,
  generateContentNetworkOptionsList,
  generateContentTitleOptionsList,
  generateLookupKeyOptionsList,
  TInventoryRulesOrderByKey,
} from '~/src/dash/apiV2/inventory-rules/inventory-rules';
import {
  convertSetIntoArrayOfNumbers,
  convertSetIntoArrayOfStrings,
} from '~/src/dash/components/filters/conversions';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';

export const formatDataToLVTRows = (
  data: IInventoryRules[],
  cfg: IAllRulesTable,
): IListViewTableRow[] => {
  const rows = [] as IListViewTableRow[];
  data.forEach((datum) => {
    const row = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof IInventoryRules];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(row);
  });
  return rows;
};

interface IAllRulesTable {
  columns: IListViewTableColumns;
}

export interface IAllRulesConfig {
  cypressClassName: string;
  startToEndDateRanges: IStartToEndDateRanges;
  defaultState: {
    activeColumnKeys: string[];
    isTableExpanded: boolean;
    orderDirection: orderDirection;
    orderBy: TInventoryRulesOrderByKey;
    page: number;
    rowsPerPage: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: string[];
  };
  table: IAllRulesTable;
  filters: Record<string, IExtendedIFilterRowObject>;
}

export const allRulesViewConfig = {
  cypressClassName: 'InventoryRulesAllRules',
  startToEndDateRanges: {
    startDateFrom: null,
    startDateTo: null,
    endDateFrom: null,
    endDateTo: null,
  },
  defaultState: {
    activeColumnKeys: [
      'Key',
      'Value',
      'StartTime',
      'EndTime',
      'ContentTitle',
      'ContentGenre',
      'ContentCategory',
      'ContentNetwork',
    ],
    isTableExpanded: false,
    orderDirection: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 25,
  },
  columnSelector: {
    resetKeys: ['ID'],
  },
  filters: {
    RuleType: {
      key: 'RuleType',
      label: 'Format Type',
      tKey: 'FormatType',
      getList: (_session, _user) => {
        return Promise.resolve([
          { id: RuleType.VMAP, label: 'VMAP' },
          { id: RuleType.VAST, label: 'VAST' },
          { id: RuleType.ORTB, label: 'ORTB' },
        ]);
      },
      queryKey: 'RuleType',
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    LookupKey: {
      key: 'LookupKey',
      label: 'Key',
      tKey: 'Key',
      getList: generateLookupKeyOptionsList,
      queryKey: 'LookupKey',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentCategory: {
      key: 'ContentCategory',
      label: 'Category',
      tKey: 'Category',
      getList: generateContentCategoriesOptionsList,
      queryKey: 'ContentCategory',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentGenre: {
      key: 'ContentGenre',
      label: 'Genre',
      tKey: 'Genre',
      getList: generateContentGenreOptionsList,
      queryKey: 'ContentGenre',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentNetwork: {
      key: 'ContentNetwork',
      label: 'Network',
      tKey: 'Network',
      getList: generateContentNetworkOptionsList,
      queryKey: 'ContentNetwork',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentTitle: {
      key: 'ContentTitle',
      label: 'Title',
      tKey: 'Title',
      getList: generateContentTitleOptionsList,
      queryKey: 'ContentTitle',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
  } as Record<string, IExtendedIFilterRowObject>,
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
        orderByKey: 'ID',
      },
      CreatedAt: {
        formatValue: (value: string) => new Date(value).toLocaleString(),
        isHidden: true,
        isSearchable: true,
        key: 'CreatedAt',
        label: 'Created At',
        requestKey: 'CreatedAt',
        responseKey: 'CreatedAt',
        valueType: 'string',
      },
      Name: {
        // Format Type
        formatValue: (value: number) => convertRuleTypeToString(value),
        cellRenderType: 'string',
        cellStyle: {
          fontWeight: FONT_WEIGHT.base,
          paddingTop: '16px',
          paddingBottom: '16px',
        },
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        tKey: 'FormatType',
        label: 'Format Type',
        requestKey: '',
        responseKey: 'RuleType',
        valueType: 'string',
        orderByKey: 'Name',
      },
      Key: {
        isSearchable: true,
        disableSort: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Key',
        tKey: 'Key',
        label: 'Key',
        requestKey: '',
        responseKey: 'LookupKey',
        valueType: 'string',
      },
      Value: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Value',
        tKey: 'Value',
        label: 'Value',
        requestKey: '',
        responseKey: 'LookupValue',
        valueType: 'string',
        orderByKey: 'Value',
      },
      StartTime: {
        cellJSX: DateTimeSecondsRender,
        isSearchable: true,
        headerRenderType: 'date',
        cellRenderType: 'date',
        key: 'StartTime',
        tKey: 'StartTime',
        label: 'Start Time',
        requestKey: '',
        responseKey: 'StartDateTime',
        valueType: 'string',
        orderByKey: 'StartTime',
      },
      EndTime: {
        cellJSX: DateTimeSecondsRender,
        isSearchable: true,
        headerRenderType: 'date',
        cellRenderType: 'date',
        key: 'EndTime',
        tKey: 'EndTime',
        label: 'End Time',
        requestKey: '',
        responseKey: 'EndDateTime',
        valueType: 'string',
        orderByKey: 'EndTime',
      },
      ContentCategory: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentCategory',
        tKey: 'ContentCategory',
        label: 'Content Category',
        requestKey: '',
        responseKey: 'ContentCategory',
        valueType: 'string',
        orderByKey: 'ContentCategory',
      },
      ContentChannel: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentChannel',
        tKey: 'ContentChannel',
        label: 'Content Channel',
        requestKey: '',
        responseKey: 'ContentChannel',
        valueType: 'string',
        orderByKey: 'ContentChannel',
      },
      ContentContext: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentContext',
        tKey: 'ContentContext',
        label: 'Content Context',
        requestKey: '',
        responseKey: 'ContentContext',
        valueType: 'string',
        orderByKey: 'ContentContext',
      },
      ContentDistributionID: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentDistributionID',
        tKey: 'ContentDistributionID',
        label: 'Content Distribution ID',
        requestKey: '',
        responseKey: 'ContentDistributionID',
        valueType: 'string',
        orderByKey: 'ContentDistributionID',
      },
      ContentDistributionName: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentDistributionName',
        tKey: 'ContentDistributionName',
        label: 'Content Distribution Name',
        requestKey: '',
        responseKey: 'ContentDistributionName',
        valueType: 'string',
        orderByKey: 'ContentDistributionName',
      },
      ContentEpisode: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentEpisode',
        tKey: 'ContentEpisode',
        label: 'Content Episode',
        requestKey: '',
        responseKey: 'ContentEpisode',
        valueType: 'string',
        orderByKey: 'ContentEpisode',
      },
      ContentGenre: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentGenre',
        tKey: 'ContentGenre',
        label: 'Content Genre',
        requestKey: '',
        responseKey: 'ContentGenre',
        valueType: 'string',
        orderByKey: 'ContentGenre',
      },
      ContentID: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentID',
        tKey: 'ContentID',
        label: 'Content ID',
        requestKey: '',
        responseKey: 'ContentID',
        valueType: 'string',
        orderByKey: 'ContentID',
      },
      ContentKeywords: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentKeywords',
        tKey: 'ContentKeywords',
        label: 'Content Keywords',
        requestKey: '',
        responseKey: 'ContentKeywords',
        valueType: 'string',
        orderByKey: 'ContentKeywords',
      },
      ContentLanguage: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentLanguage',
        tKey: 'ContentLanguage',
        label: 'Content Language',
        requestKey: '',
        responseKey: 'ContentLanguage',
        valueType: 'string',
        orderByKey: 'ContentLanguage',
      },
      ContentLength: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentLength',
        tKey: 'ContentLength',
        label: 'Content Length',
        requestKey: '',
        responseKey: 'ContentLength',
        valueType: 'string',
        orderByKey: 'ContentLength',
      },
      ContentNetwork: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentNetwork',
        tKey: 'ContentNetwork',
        label: 'Content Network',
        requestKey: '',
        responseKey: 'ContentNetwork',
        valueType: 'string',
        orderByKey: 'ContentNetwork',
      },
      ContentProdQuality: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentProdQuality',
        tKey: 'ContentProdQuality',
        label: 'Content Prod Quality',
        requestKey: '',
        responseKey: 'ContentProdQuality',
        valueType: 'string',
        orderByKey: 'ContentProdQuality',
      },
      ContentProducerID: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentProducerID',
        tKey: 'ContentProducerID',
        label: 'Content Producer ID',
        requestKey: '',
        responseKey: 'ContentProducerID',
        valueType: 'string',
        orderByKey: 'ContentProducerID',
      },
      ContentProducer: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentProducer',
        tKey: 'ContentProducer',
        label: 'Content Producer',
        requestKey: '',
        responseKey: 'ContentProducer',
        valueType: 'string',
        orderByKey: 'ContentProducer',
      },
      ContentRating: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentRating',
        tKey: 'ContentRating',
        label: 'Content Rating',
        requestKey: '',
        responseKey: 'ContentRating',
        valueType: 'string',
        orderByKey: 'ContentRating',
      },
      ContentSeason: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentSeason',
        tKey: 'ContentSeason',
        label: 'Content Season',
        requestKey: '',
        responseKey: 'ContentSeason',
        valueType: 'string',
        orderByKey: 'ContentSeason',
      },
      ContentSeries: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentSeries',
        tKey: 'ContentSeries',
        label: 'Content Series',
        requestKey: '',
        responseKey: 'ContentSeries',
        valueType: 'string',
        orderByKey: 'ContentSeries',
      },
      ContentTaxonomy: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentTaxonomy',
        tKey: 'ContentTaxonomy',
        label: 'Content Taxonomy',
        requestKey: '',
        responseKey: 'ContentTaxonomy',
        valueType: 'string',
        orderByKey: 'ContentTaxonomy',
      },
      ContentTitle: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentTitle',
        tKey: 'ContentTitle',
        label: 'Content Title',
        requestKey: '',
        responseKey: 'ContentTitle',
        valueType: 'string',
        orderByKey: 'ContentTitle',
      },
      ContentURL: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ContentURL',
        tKey: 'ContentURL',
        label: 'Content URL',
        requestKey: '',
        responseKey: 'ContentURL',
        valueType: 'string',
        orderByKey: 'ContentURL',
      },
      ResponseType: {
        isHidden: true,
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ResponseType',
        label: 'Response Type',
        requestKey: '',
        responseKey: 'ResponseType',
        valueType: 'string',
      },
      TimezoneType: {
        isHidden: true,
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'TimezoneType',
        label: 'Timezone Type',
        requestKey: '',
        responseKey: 'TimezoneType',
        valueType: 'string',
      },
      AdBreaks: {
        disableSort: true,
        defaultOrderDirection: 'asc',
        isHidden: true,
        isSearchable: true,
        key: 'AdBreaks',
        label: 'Ad Breaks',
        requestKey: '',
        responseKey: 'AdBreaks',
        valueType: 'component',
      },
      Custom5: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom5',
        tKey: 'Custom5',
        label: 'Custom 5',
        requestKey: '',
        responseKey: 'Custom5',
        valueType: 'string',
        orderByKey: 'Custom5',
      },
      Custom6: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom6',
        tKey: 'Custom6',
        label: 'Custom 6',
        requestKey: '',
        responseKey: 'Custom6',
        valueType: 'string',
        orderByKey: 'Custom6',
      },
      Custom7: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom7',
        tKey: 'Custom7',
        label: 'Custom 7',
        requestKey: '',
        responseKey: 'Custom7',
        valueType: 'string',
        orderByKey: 'Custom7',
      },
      Custom8: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom8',
        tKey: 'Custom8',
        label: 'Custom 8',
        requestKey: '',
        responseKey: 'Custom8',
        valueType: 'string',
        orderByKey: 'Custom8',
      },
      Custom9: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom9',
        tKey: 'Custom9',
        label: 'Custom 9',
        requestKey: '',
        responseKey: 'Custom9',
        valueType: 'string',
        orderByKey: 'Custom9',
      },
      Custom10: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom10',
        tKey: 'Custom10',
        label: 'Custom 10',
        requestKey: '',
        responseKey: 'Custom10',
        valueType: 'string',
        orderByKey: 'Custom10',
      },
      Custom11: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom11',
        tKey: 'Custom11',
        label: 'Custom 11',
        requestKey: '',
        responseKey: 'Custom11',
        valueType: 'string',
        orderByKey: 'Custom11',
      },
      Custom12: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom12',
        tKey: 'Custom12',
        label: 'Custom 12',
        requestKey: '',
        responseKey: 'Custom12',
        valueType: 'string',
        orderByKey: 'Custom12',
      },
      Custom13: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom13',
        tKey: 'Custom13',
        label: 'Custom 13',
        requestKey: '',
        responseKey: 'Custom13',
        valueType: 'string',
        orderByKey: 'Custom13',
      },
      Custom14: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom14',
        tKey: 'Custom14',
        label: 'Custom 14',
        requestKey: '',
        responseKey: 'Custom14',
        valueType: 'string',
        orderByKey: 'Custom14',
      },
      Custom15: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom15',
        tKey: 'Custom15',
        label: 'Custom 15',
        requestKey: '',
        responseKey: 'Custom15',
        valueType: 'string',
        orderByKey: 'Custom15',
      },
      Custom16: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom16',
        tKey: 'Custom16',
        label: 'Custom 16',
        requestKey: '',
        responseKey: 'Custom16',
        valueType: 'string',
        orderByKey: 'Custom16',
      },
      Custom17: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom17',
        tKey: 'Custom17',
        label: 'Custom 17',
        requestKey: '',
        responseKey: 'Custom17',
        valueType: 'string',
        orderByKey: 'Custom17',
      },
      Custom18: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Custom18',
        tKey: 'Custom18',
        label: 'Custom 18',
        requestKey: '',
        responseKey: 'Custom18',
        valueType: 'string',
        orderByKey: 'Custom18',
      },
    },
  },
} as IAllRulesConfig;
